import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { wechatService, WechatUserInfoResponse } from '../services/wechatService';

/**
 * 微信授权处理组件
 * 处理微信公众号跳转过来的授权码，获取用户openid后跳转到登录页面
 */
const WechatAuthHandler: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
    const [message, setMessage] = useState('正在处理微信授权...');

    /**
     * 调用后端接口获取用户信息
     */
    const getUserInfo = async (code: string): Promise<WechatUserInfoResponse> => {
        return await wechatService.getUserInfo(code);
    };

    useEffect(() => {
        const processWechatAuth = async () => {
            try {
                // 使用wechatService获取URL参数
                const code = wechatService.getCodeFromUrl();
                const state = wechatService.getStateFromUrl();
                const { error, errorDescription } = wechatService.getErrorFromUrl();

                // 检查是否有错误
                if (error) {
                    setStatus('error');
                    setMessage(`微信授权失败: ${errorDescription || error}`);
                    return;
                }

                // 检查必要参数
                if (!code) {
                    setStatus('error');
                    setMessage('缺少授权码，请重新授权');
                    return;
                }

                console.log('收到微信授权回调，code:', code, 'state:', state);

                // 调用后端接口获取用户信息
                const userInfo = await getUserInfo(code);

                if (userInfo.success && userInfo.openid) {
                    setStatus('success');
                    setMessage('微信授权成功，正在进入系统...');

                    console.log('获取到用户openid:', userInfo.openid);

                    // 保存 openid 到 localStorage
                    localStorage.setItem('weChatOpenId', userInfo.openid);

                    // 清理URL参数
                    wechatService.cleanUrlParams();

                    // 延迟跳转，让用户看到成功信息
                    setTimeout(() => {
                        // 跳转到首页，AuthRoute 会自动处理跳转逻辑
                        // - 已注册用户：直接进入首页
                        // - 未注册用户：跳转到提交资料页面
                        navigate(`/?weChatOpenId=${userInfo.openid}`, { replace: true });
                    }, 1500);
                } else {
                    setStatus('error');
                    setMessage(userInfo.message || '获取用户信息失败');
                }
            } catch (err) {
                console.error('处理微信授权失败:', err);
                setStatus('error');
                setMessage(err instanceof Error ? err.message : '处理授权时发生错误');
            }
        };

        processWechatAuth();
    }, [location.search, navigate]);

    /**
     * 重新授权
     */
    const handleRetry = () => {
        navigate('/', { replace: true });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                {/* 状态图标 */}
                <div className="mb-6">
                    {status === 'processing' && (
                        <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}
                    {status === 'success' && (
                        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                    )}
                    {status === 'error' && (
                        <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                    )}
                </div>

                {/* 状态标题 */}
                <h2 className="text-2xl font-bold mb-4 text-gray-800">
                    {status === 'processing' && '处理中'}
                    {status === 'success' && '授权成功'}
                    {status === 'error' && '授权失败'}
                </h2>

                {/* 状态消息 */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                    {message}
                </p>

                {/* 操作按钮 */}
                {status === 'error' && (
                    <div className="space-y-3">
                        <button
                            onClick={handleRetry}
                            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                        >
                            返回首页
                        </button>
                    </div>
                )}

                {status === 'processing' && (
                    <div className="text-sm text-gray-500">
                        请稍候，正在处理您的授权信息...
                    </div>
                )}

                {status === 'success' && (
                    <div className="text-sm text-gray-500">
                        即将自动进入系统...
                    </div>
                )}
            </div>
        </div>
    );
};

export default WechatAuthHandler;
